import {Component, computed, inject, OnInit} from '@angular/core';
import {GuiaComponent} from '../../../../shared/components/guia-component/guia-component';
import {ResultStateService} from '../../services/result.state.service';
import {Resultado} from '../../interface/result.inteface';
import {Router} from '@angular/router';
import {AnswerResponseDto} from '../../interface/survey-responses.interface';
import { EmptyStateComponent } from '@/app/shared/components/empty-state/empty-state.component';
import { ThemeService } from '@/app/core/services/theme/theme.service';

@Component({
  selector: 'app-guia1-component',
  imports: [GuiaComponent, EmptyStateComponent],
  templateUrl: './guia1-component.html',
  standalone: true,
  styleUrl: './guia1-component.scss'
})
export class Guia1Component implements OnInit {
  private resultStateService = inject(ResultStateService);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private cuestionario!: Resultado | null;
  protected themeContent = this.themeService.themeContent;
  protected guideContent = computed(() => this.themeContent()?.guideOneComponent);

  // Reemplazamos el mock data con data real
  guia1: any[] = [];

  handleGoBack(){
    this.router.navigate(['resultados']);
  }

  ngOnInit(): void {
    this.cuestionario = this.resultStateService.getCuestionario();

    // Obtener la data real de las respuestas para esta guía específica
    if (this.cuestionario) {
      const surveyAnswers = this.resultStateService.getSurveyAnswersForGuide(this.cuestionario.token, this.cuestionario.guideId!);

      // Si no hay data real, usar mock data como fallback
      if (surveyAnswers && surveyAnswers.length > 0) {
        this.guia1 = this.transformSurveyAnswersToGuiaFormat(surveyAnswers);
      }
    }
  }

  private normalizeGuideId(guideId: string): string {
    return guideId.toUpperCase().replace('-', '_');
  }

  /**
   * Transforma las respuestas del API al formato que espera GuiaComponent
   */
  private transformSurveyAnswersToGuiaFormat(answers: AnswerResponseDto[]): any[] {

    return answers.map(answer => {

      // Buscar la pregunta correspondiente en el mock data para mantener la estructura
      const mockQuestion = this.getMockQuestionByCode(answer.questionCode);

      const transformedAnswer = {
        questionCode: answer.questionCode,
        category: mockQuestion?.category || 'Acontecimiento traumático severo',
        domain: mockQuestion?.domain || 'Acontecimiento traumático',
        dimension: mockQuestion?.dimension || 'Respuesta',
        score: Number(answer.responseValue) || 0,
        inverted: mockQuestion?.inverted || false,
        text: mockQuestion?.text || `Pregunta ${answer.questionCode}`
      };

      return transformedAnswer;
    });
  }

  /**
   * Busca la pregunta correspondiente en el mock data por questionCode
   */
  private getMockQuestionByCode(questionCode: string): any {
    const mockData = [
      {
        "questionCode": "GI_I_1",
        "category": "Acontecimiento traumático severo",
        "domain": "Acontecimiento traumático",
        "dimension": "Accidente grave",
        "score": 0,
        "inverted": false,
        "text": "¿Ha presenciado o sufrido alguna vez, durante o con motivo del trabajo un accidente que tenga como consecuencia la muerte, la pérdida de un miembro o una lesión grave?"
      },
      {
        "questionCode": "GI_I_2",
        "category": "Acontecimiento traumático severo",
        "domain": "Acontecimiento traumático",
        "dimension": "Asalto",
        "score": 0,
        "inverted": false,
        "text": "¿Ha presenciado o sufrido alguna vez, durante o con motivo del trabajo asaltos?"
      },
      {
        "questionCode": "GI_I_3",
        "category": "Acontecimiento traumático severo",
        "domain": "Acontecimiento traumático",
        "dimension": "Acto violento grave",
        "score": 0,
        "inverted": false,
        "text": "¿Ha presenciado o sufrido alguna vez, durante o con motivo del trabajo actos violentos que derivaron en lesiones graves?"
      },
      {
        "questionCode": "GI_I_4",
        "category": "Acontecimiento traumático severo",
        "domain": "Acontecimiento traumático",
        "dimension": "Secuestro",
        "score": 0,
        "inverted": false,
        "text": "¿Ha presenciado o sufrido alguna vez, durante o con motivo del trabajo secuestro?"
      },
      {
        "questionCode": "GI_I_5",
        "category": "Acontecimiento traumático severo",
        "domain": "Acontecimiento traumático",
        "dimension": "Amenaza",
        "score": 0,
        "inverted": false,
        "text": "¿Ha presenciado o sufrido alguna vez, durante o con motivo del trabajo amenazas?"
      },
      {
        "questionCode": "GI_I_6",
        "category": "Acontecimiento traumático severo",
        "domain": "Acontecimiento traumático",
        "dimension": "Riesgo a la vida/salud",
        "score": 0,
        "inverted": false,
        "text": "¿Ha presenciado o sufrido alguna vez, durante o con motivo del trabajo cualquier otro que ponga en riesgo su vida o salud, y/o la de otras personas?"
      },
      {
        "questionCode": "GI_II_1",
        "category": "Acontecimiento traumático severo",
        "domain": "Recuerdos persistentes",
        "dimension": "Recuerdos malestar",
        "score": 0,
        "inverted": false,
        "text": "¿Ha tenido recuerdos recurrentes sobre el acontecimiento que le provocan malestares?"
      },
      {
        "questionCode": "GI_II_2",
        "category": "Acontecimiento traumático severo",
        "domain": "Recuerdos persistentes",
        "dimension": "Sueños malestar",
        "score": 0,
        "inverted": false,
        "text": "¿Ha tenido sueños de carácter recurrente sobre el acontecimiento, que le producen malestar?"
      },
      {
        "questionCode": "GI_III_1",
        "category": "Acontecimiento traumático severo",
        "domain": "Evitación",
        "dimension": "Evitar sentimientos/situaciones",
        "score": 0,
        "inverted": false,
        "text": "¿Se ha esforzado por evitar todo tipo de sentimientos, conversaciones o situaciones que le puedan recordar el acontecimiento?"
      },
      {
        "questionCode": "GI_III_2",
        "category": "Acontecimiento traumático severo",
        "domain": "Evitación",
        "dimension": "Evitar actividades/lugares/personas",
        "score": 0,
        "inverted": false,
        "text": "¿Se ha esforzado por evitar todo tipo de actividades, lugares o personas que motivan recuerdos del acontecimiento?"
      },
      {
        "questionCode": "GI_III_3",
        "category": "Acontecimiento traumático severo",
        "domain": "Evitación",
        "dimension": "Dificultad para recordar",
        "score": 0,
        "inverted": false,
        "text": "¿Ha tenido dificultad para recordar alguna parte importante del evento?"
      },
      {
        "questionCode": "GI_III_4",
        "category": "Acontecimiento traumático severo",
        "domain": "Evitación",
        "dimension": "Disminución interés",
        "score": 0,
        "inverted": false,
        "text": "¿Ha disminuido su interés en sus actividades cotidianas?"
      },
      {
        "questionCode": "GI_III_5",
        "category": "Acontecimiento traumático severo",
        "domain": "Evitación",
        "dimension": "Alejamiento",
        "score": 0,
        "inverted": false,
        "text": "¿Se ha sentido usted alejado o distante de los demás?"
      },
      {
        "questionCode": "GI_III_6",
        "category": "Acontecimiento traumático severo",
        "domain": "Evitación",
        "dimension": "Dificultad expresar sentimientos",
        "score": 0,
        "inverted": false,
        "text": "¿Ha notado que tiene dificultad para expresar sus sentimientos?"
      },
      {
        "questionCode": "GI_III_7",
        "category": "Acontecimiento traumático severo",
        "domain": "Evitación",
        "dimension": "Futuro limitado",
        "score": 0,
        "inverted": false,
        "text": "¿Ha tenido la impresión de que su vida se va a acortar, que va a morir antes que otras personas o que tiene un futuro limitado?"
      },
      {
        "questionCode": "GI_IV_1",
        "category": "Acontecimiento traumático severo",
        "domain": "Afectación",
        "dimension": "Dificultad dormir",
        "score": 0,
        "inverted": false,
        "text": "¿Ha tenido usted dificultades para dormir?"
      },
      {
        "questionCode": "GI_IV_2",
        "category": "Acontecimiento traumático severo",
        "domain": "Afectación",
        "dimension": "Irritabilidad",
        "score": 0,
        "inverted": false,
        "text": "¿Ha estado particularmente irritable o le han dado arranques de coraje?"
      },
      {
        "questionCode": "GI_IV_3",
        "category": "Acontecimiento traumático severo",
        "domain": "Afectación",
        "dimension": "Dificultad concentrarse",
        "score": 0,
        "inverted": false,
        "text": "¿Ha tenido dificultad para concentrarse?"
      },
      {
        "questionCode": "GI_IV_4",
        "category": "Acontecimiento traumático severo",
        "domain": "Afectación",
        "dimension": "Nerviosismo/alerta",
        "score": 0,
        "inverted": false,
        "text": "¿Ha estado nervioso o constantemente en alerta?"
      },
      {
        "questionCode": "GI_IV_5",
        "category": "Acontecimiento traumático severo",
        "domain": "Afectación",
        "dimension": "Sobresalto fácil",
        "score": 0,
        "inverted": false,
        "text": "¿Se ha sobresaltado fácilmente por cualquier cosa?"
      }
    ];

    return mockData.find(q => q.questionCode === questionCode);
  }
}
